import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { useRsColors } from '@/hooks/useRsColors';
import { getChartFontSize } from '@/utils/chartTypography';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { getDefaultPreviousYearWindowFromLabels } from '@/utils/chartWindow';
import { getDateLabelWindowInfo, shouldShowDataLabelsByFrequency } from '@/utils/chartDataLabelPolicy';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface PolymerBarChartProps {
  data: number[];
  labels: string[];
  label: string;
  color?: string;
  showLabels?: boolean;
  yAxisLabel?: string;
  className?: string;
  autoHideLabelsThreshold?: number;
  showRangeSlider?: boolean;
  xTickStride?: number;
  dataLabelColor?: string;
}

const PolymerBarChart: React.FC<PolymerBarChartProps> = ({
  data,
  labels,
  label,
  color,
  showLabels = false,
  yAxisLabel,
  className,
  autoHideLabelsThreshold = 24,
  showRangeSlider = true,
  xTickStride,
  dataLabelColor,
}) => {
  const rs = useRsColors();

  const defaultDateWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(labels),
    [labels],
  );

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    defaultStartIndex: defaultDateWindow.startIndex,
    defaultEndIndex: defaultDateWindow.endIndex,
  });

  const visibleLabels = useMemo(
    () => (showRangeSlider ? labels.slice(startIndex, endIndex + 1) : labels),
    [showRangeSlider, labels, startIndex, endIndex],
  );

  const visibleData = useMemo(
    () => (showRangeSlider ? data.slice(startIndex, endIndex + 1) : data),
    [showRangeSlider, data, startIndex, endIndex],
  );

  const labelWindowInfo = useMemo(
    () => getDateLabelWindowInfo(visibleLabels),
    [visibleLabels],
  );

  const shouldShowBarLabels = useMemo(() => {
    const byFrequency = shouldShowDataLabelsByFrequency({
      enabled: showLabels,
      labels: visibleLabels,
      maxDailyWindowDays: 365,
    });

    if (!byFrequency) return false;
    if (!labelWindowInfo.hasDateInfo || !labelWindowInfo.isDailyLike) return true;
    return visibleData.length <= autoHideLabelsThreshold;
  }, [showLabels, visibleLabels, visibleData.length, autoHideLabelsThreshold, labelWindowInfo]);

  const barColor = color || rs.green || '#80ed40';

  const barColors = visibleData.map((v) => {
    if (v < 0) return rs.red || '#ef4444';
    return barColor;
  });

  const chartData = {
    labels: visibleLabels,
    datasets: [
      {
        label,
        data: visibleData,
        backgroundColor: barColors,
        borderRadius: 2,
        borderSkipped: false,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, right: 12, bottom: 8, left: 8 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
      },
      datalabels: {
        display: shouldShowBarLabels,
        anchor: 'center',
        align: 'center',
        color: dataLabelColor || rs.offWhite || '#f3f4f6',
        font: { size: getChartFontSize() },
        formatter: (value: number) => (value !== null && value !== undefined ? value.toFixed(4) : ''),
      },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          ...(xTickStride && xTickStride > 1
            ? {
                autoSkip: false,
                maxRotation: 0,
                callback: function (this: any, value: any, index: number) {
                  const lastIndex = visibleData.length - 1;
                  return (lastIndex - index) % xTickStride === 0 ? this.getLabelForValue(value) : '';
                },
              }
            : visibleData.length > 24
              ? {
                  autoSkip: true,
                  maxTicksLimit: Math.ceil(visibleData.length / 6),
                  maxRotation: 45,
                }
              : {}),
        },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: { color: rs.offWhite || '#f3f4f6', font: { size: getChartFontSize() } },
        grid: { color: rs.gridLine },
        ...(yAxisLabel ? { title: { display: true, text: yAxisLabel, color: rs.offWhite || '#f3f4f6', font: { size: getChartFontSize() } } } : {}),
      },
    },
  };

  return (
    <div className={className || 'w-full h-full bg-rs-jet rounded'} data-testid="polymer-bar-chart">
      <div className={showRangeSlider && hasWindowing ? 'h-[calc(100%-50px)] w-full' : 'h-full w-full'}>
        <Bar data={chartData} options={options} />
      </div>
      {showRangeSlider && hasWindowing && (
        <ChartRangeSlider
          totalPoints={labels.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onChange={setWindow}
        />
      )}
    </div>
  );
};

export default PolymerBarChart;
